import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Truck as TruckIcon, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  Droplets, Container, Package, Bus, Truck, MoreHorizontal, AlertTriangle, Upload, Trash2,
  FileBadge,
} from 'lucide-react';
import { UploadStatusRow } from '@/components/common/UploadStatusRow';
import { useQuery } from '@tanstack/react-query';
import { RequestChangeDialog, diffFields, type ChangeRequestDraft } from '@/components/common/RequestChange';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import {
  useVehicles, useCreateVehicle, useUpdateVehicle, useExpiringDocuments,
} from '@/hooks/useVehicles';
import { VehicleForm } from './VehicleForm';
import { ExcelToolbar } from '@/components/excel/ExcelToolbar';
import { ProjectSwitcher } from '@/components/common/ProjectSwitcher';
import { ComplianceBadge, computeComplianceStatus } from '@/components/ComplianceBadge';
import { PlateBadge } from '@/components/PlateBadge';
import type { Vehicle, VehicleType, VehicleStatus } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type StatusFilter = 'all' | VehicleStatus;
type TypeFilter = 'all' | VehicleType;

const VEHICLE_TYPE_ICONS: Record<VehicleType, typeof Droplets> = {
  water_tanker: Droplets,
  sewage_tanker: Droplets,
  container_truck: Container,
  flatbed: Package,
  dumper: Truck,
  pickup: Truck,
  bus: Bus,
  other: MoreHorizontal,
};

export default function VehiclesPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  // PR-VEH-FIX Issue 2 MAY 11 — delete vehicle modal (super_admin only)
  // PR-PERMS-CLIENTS MAY 12 — admin can delete (no-force).
  // Direct action is super_admin ONLY; an admin now goes through the queue.
  const isSuperAdmin = user?.role === 'super_admin';
  const [crDraft, setCrDraft] = useState<ChangeRequestDraft | null>(null);
  const [crNote, setCrNote] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Vehicle | null>(null);
  // This month's fuel per vehicle, split by the card that paid for it. Same
  // endpoint the Sides screen uses - two queries answering one question is how
  // two screens end up disagreeing about the same truck.
  const { data: fuelByVehicle } = useQuery({
    queryKey: ['fuel-by-vehicle-month'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Array<{
          vehicleId: string; fills: number; liters: number; sar: number;
          byProvider: Record<string, { fills: number; liters: number; sar: number }>;
        }>;
      }>('/api/v1/fuel-by-vehicle-month');
      return new Map(data.items.map((r) => [r.vehicleId, r]));
    },
    staleTime: 5 * 60 * 1000,
  });

  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // PR-FD MAY 11 — counts surfaced from 409 + force-delete opt-in
  interface DepCounts { trips: number; flatbed_trips: number; maintenance_events: number; advances: number; collections: number; fuel_transactions: number }
  const [deleteCounts, setDeleteCounts] = useState<DepCounts | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  function openDeleteFor(v: Vehicle) {
    setDeleting(v); setDeleteReason(''); setDeleteConfirm('');
    setDeleteError(null); setDeleteCounts(null); setForceDelete(false);
  }
  async function performDelete() {
    if (!deleting) return;
    if (deleteConfirm !== 'DELETE') { setDeleteError('Type DELETE to confirm'); return; }
    if (deleteReason.trim().length < 3) { setDeleteError('Reason is required'); return; }
    setDeleteBusy(true); setDeleteError(null);
    try {
      const url = '/api/v1/vehicles/' + deleting.id + (forceDelete ? '?force=true' : '');
      await apiClient.delete(url, { data: { reason: deleteReason.trim() } });
      setDeleting(null); setDeleteReason(''); setDeleteConfirm(''); setForceDelete(false); setDeleteCounts(null);
      await refetch();
    } catch (e) {
      const errResp = (e as { response?: { data?: { reason?: string; counts?: DepCounts } } })?.response?.data;
      if (errResp?.counts) setDeleteCounts(errResp.counts);
      setDeleteError(errResp?.reason ?? extractErrorMessage(e));
    } finally { setDeleteBusy(false); }
  }

  useEffect(() => {
    const tm = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      vehicleType: typeFilter === 'all' ? undefined : typeFilter,
      page,
      pageSize: 25,
    }),
    [search, statusFilter, typeFilter, page]
  );

  const { data, isLoading, isError, error, refetch } = useVehicles(params);
  const { data: expiringData } = useExpiringDocuments(30);
  const createMutation = useCreateVehicle();
  const updateMutation = useUpdateVehicle();

  const vehicles = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const expiringTotal = expiringData?.total ?? 0;

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };

  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    if (!isSuperAdmin) {
      const fields = diffFields(
        editing as unknown as Record<string, unknown>,
        input as unknown as Record<string, unknown>,
      );
      const label = `${editing.code} · ${editing.plateEn}`;
      setEditing(null);
      setCrDraft({ tableName: 'vehicles', recordId: editing.id, label, action: 'update', fields });
      return;
    }
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';
  const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'under_maintenance', 'inactive', 'sold'];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />
              {t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <TruckIcon className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('dashboard.modules.vehicles')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('vehicles.subtitle')}</p>
          </div>

          {/* Action buttons grouped */}
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/dashboard/vehicles/bodies">
              <Button variant="outline" size="lg">
                <Package className="h-4 w-4" />
                {t('vehicles.openBodies')}
              </Button>
            </Link>
            <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
              <Plus className="h-4 w-4" />
              {t('vehicles.addVehicle')}
            </Button>
          </div>
        </div>

        {/* Bulk Excel: export current fleet, edit in Excel, upload back */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Export only, as on the drivers list. An upload here can rewrite every
                vehicle in one action; that belongs on the import screen, which
                previews the change before it commits. */}
            <ExcelToolbar entity="vehicles" title="vehicles" exportOnly />
            {/* Which side you are looking at. The list is filtered on it
                exactly, so anything not filed under a side is only visible
                under "All sides". */}
            <ProjectSwitcher />
          </div>
        </div>

        {/* Expiring documents callout */}
        {expiringTotal > 0 && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {t('vehicles.expiringAlert', { count: expiringTotal })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('vehicles.expiringHelp')}
              </p>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-card border border-border rounded-lg p-4 mb-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('vehicles.searchPlaceholder')}
                className="ps-10"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value as TypeFilter); setPage(1); }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">{t('vehicles.filter.allTypes')}</option>
              <option value="water_tanker">{t('vehicles.type.water_tanker')}</option>
              <option value="sewage_tanker">{t('vehicles.type.sewage_tanker')}</option>
              <option value="container_truck">{t('vehicles.type.container_truck')}</option>
              <option value="flatbed">{t('vehicles.type.flatbed')}</option>
              <option value="pickup">{t('vehicles.type.pickup')}</option>
              <option value="bus">{t('vehicles.type.bus')}</option>
              <option value="other">{t('vehicles.type.other')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                {t(`vehicles.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="py-20 text-center">
              <TruckIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' || typeFilter !== 'all'
                  ? t('vehicles.noResults')
                  : t('vehicles.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' || typeFilter !== 'all'
                  ? t('vehicles.tryDifferentSearch')
                  : t('vehicles.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && typeFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('vehicles.addFirstVehicle')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.code')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.plate')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.type')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.brandModel')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.investor')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.compliance')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">DOCS</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">FUEL THIS MONTH</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('vehicles.column.status')}</th>
                      {isSuperAdmin && (
                        <th className="text-end px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-12">{/* delete column */}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {vehicles.map((v) => {
                      const TypeIcon = VEHICLE_TYPE_ICONS[v.vehicleType] ?? MoreHorizontal;
                      const compliance = computeComplianceStatus(v);
                      return (
                        <tr
                          key={v.id}
                          onClick={() => setEditing(v)}
                          className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{v.code}</td>
                          <td className="px-4 py-3">
                            <PlateBadge plate={v.plateEn} plateAr={v.plateAr} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="inline-flex items-center gap-1.5 text-foreground">
                              <TypeIcon className="h-4 w-4 text-muted-foreground" />
                              <span>{t(`vehicles.type.${v.vehicleType}`)}</span>
                            </div>
                            {v.targetTonnageM3 != null && (v.vehicleType === 'water_tanker' || v.vehicleType === 'sewage_tanker') && (
                              <div className="text-xs text-muted-foreground mt-0.5">{Number(v.targetTonnageM3)} m³</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {v.brand || v.model ? (
                              <>
                                <div className="text-foreground">{v.brand}</div>
                                <div className="text-xs text-muted-foreground">
                                  {v.model}{v.year ? ` · ${v.year}` : ''}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-foreground">{v.investor.fullNameEn}</div>
                            <div className="text-xs text-muted-foreground font-mono">{v.investor.code}</div>
                          </td>
                          <td className="px-4 py-3">
                            <ComplianceBadge status={compliance} />
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {v.uploadStatus && (
                              <UploadStatusRow chips={[
                                { status: v.uploadStatus.istemarah,  icon: FileBadge, label: 'Istemarah',
                                  onClick: () => setEditing(v) },
                              ]} />
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {(() => {
                              const f = fuelByVehicle?.get(v.id);
                              // Absent means no card reports for this truck -
                              // a different fact from a truck that has not
                              // fuelled, so it says so rather than showing 0 L.
                              if (!f) {
                                return <span className="text-xs text-muted-foreground">no card data</span>;
                              }
                              const NAMES: Record<string, string> = {
                                petroapp: 'PetroApp', fleetplus: 'Fleet Plus',
                                aldrees: 'Aldrees', cash: 'Cash', other: 'Other',
                              };
                              // Spelled out, not abbreviated: this line is read
                              // by people checking a truck's costs, not by
                              // anyone who has memorised two-letter codes.
                              const parts = Object.entries(f.byProvider)
                                .sort((a, b) => b[1].liters - a[1].liters)
                                .map(([k, p]) => `${NAMES[k] ?? k} ${p.liters.toLocaleString()} L`);
                              return (
                                <div className="leading-tight">
                                  <div className="text-foreground">
                                    {f.liters.toLocaleString()} L
                                    <span className="text-muted-foreground text-xs">
                                      {' · '}{f.sar.toLocaleString()} SAR
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-muted-foreground">{parts.join(' · ')}</div>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={
                              v.status === 'active' ? 'active' :
                              v.status === 'under_maintenance' ? 'gold' :
                              'inactive'
                            }>
                              {t(`vehicles.status.${v.status}`)}
                            </Badge>
                          </td>
                          {isSuperAdmin && (
                            <td className="px-4 py-3 text-end">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); openDeleteFor(v); }}
                                className="text-rose-700 dark:text-rose-400 hover:bg-rose-500/10"
                                title="Delete vehicle (super_admin only)"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('vehicles.showingCount', {
                    from: (page - 1) * 25 + 1,
                    to: Math.min(page * 25, total),
                    total,
                  })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      <RequestChangeDialog
        draft={crDraft}
        onClose={() => setCrDraft(null)}
        onSent={(msg) => setCrNote(msg)}
      />
      {crNote && (
        <div className="fixed bottom-4 end-4 z-50 rounded-lg border border-emerald-500/30 bg-card p-3 text-sm shadow-lg">
          {crNote}
          <button type="button" className="ms-3 underline text-xs" onClick={() => setCrNote(null)}>dismiss</button>
        </div>
      )}

      </main>

      {/* Create drawer */}
      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('vehicles.addVehicle')}</SheetTitle>
            <SheetDescription>{t('vehicles.addVehicleDescription')}</SheetDescription>
          </SheetHeader>
          <VehicleForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('vehicles.create')} />
        </SheetContent>
      </Sheet>

      {/* Edit drawer */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              {editing?.brand ? `${editing.brand} ${editing.model ?? ''}` : editing?.plateEn ?? ''}
            </SheetTitle>
            <SheetDescription className="font-mono text-xs">
              {editing?.code} · {editing?.plateEn}
            </SheetDescription>
          </SheetHeader>
          <VehicleForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
          {/* Same reason as the drivers page: the direct delete is super_admin's,
              so everyone else needs a way to ask. */}
          {!isSuperAdmin && editing && (
            <div className="mt-6 pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  const v = editing;
                  setEditing(null);
                  setCrDraft({
                    tableName: 'vehicles',
                    recordId: v.id,
                    label: `${v.code} · ${v.plateEn}`,
                    action: 'delete',
                  });
                }}
                className="text-sm text-rose-600 dark:text-rose-400 hover:underline"
              >
                Request deletion of this vehicle
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* PR-VEH-FIX Issue 2 MAY 11 — delete confirmation (super_admin only) */}
      <Sheet open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="text-rose-700 dark:text-rose-400 inline-flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Delete vehicle?
            </SheetTitle>
            <SheetDescription>
              <span className="font-mono text-xs">{deleting?.code} · {deleting?.plateEn}</span>
              <br />This action cannot be undone.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3 text-sm">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Reason (required)</label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                placeholder="Why is this vehicle being deleted?"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Type DELETE to confirm</label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
              />
            </div>
            {deleteError && (
              <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">
                {deleteError}
              </div>
            )}
            {/* PR-FD MAY 11 — dependency counts + force-delete checkbox (super_admin only) */}
            {deleteCounts && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-xs space-y-2">
                <div className="font-medium text-amber-700 dark:text-amber-400">Dependencies that block standard delete:</div>
                <ul className="space-y-0.5 text-amber-700 dark:text-amber-400">
                  {deleteCounts.trips > 0 && <li>· {deleteCounts.trips} water-tanker trip(s)</li>}
                  {deleteCounts.flatbed_trips > 0 && <li>· {deleteCounts.flatbed_trips} flatbed trip(s)</li>}
                  {deleteCounts.maintenance_events > 0 && <li>· {deleteCounts.maintenance_events} maintenance event(s)</li>}
                  {deleteCounts.advances > 0 && <li>· {deleteCounts.advances} driver advance(s)</li>}
                  {deleteCounts.collections > 0 && <li>· {deleteCounts.collections} payment collection(s)</li>}
                  {deleteCounts.fuel_transactions > 0 && <li>· {deleteCounts.fuel_transactions} fuel transaction(s)</li>}
                </ul>
                {isSuperAdmin && (
                  <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-amber-500/30">
                    <input
                      type="checkbox"
                      checked={forceDelete}
                      onChange={(e) => setForceDelete(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-rose-600"
                    />
                    <span className="text-rose-700 dark:text-rose-400 font-medium">
                      ⚠️ Force delete — also remove every trip, maintenance event, advance, collection, and fuel transaction listed above. <em>For test data cleanup only. Do NOT use on live fleet.</em>
                    </span>
                  </label>
                )}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground italic">
              The server will refuse deletion if the vehicle has trips, an assigned
              driver, or open maintenance events. In that case the row stays;
              clean those up first, mark the vehicle inactive, or — for test data
              only — opt in to force delete above.
            </p>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
            <Button
              variant="default"
              onClick={performDelete}
              disabled={deleteBusy || deleteConfirm !== 'DELETE' || deleteReason.trim().length < 3}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-1"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {forceDelete ? 'Force delete permanently' : 'Delete permanently'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
